import { Plus } from "lucide-react"
import { cn } from "@/lib/cn"
import {
  defaultGroup,
  defaultLeaf,
  type CompositeOp,
  type CustomFieldCatalog,
  type GroupNode,
  type LeafNode,
  type PredicateNode,
  type Scope,
} from "@/lib/predicate-ast"
import { PredicateRow } from "./predicate-row"

// Recursive group renderer. Toggles ALL ↔ ANY, lets the operator add
// conditions or sub-groups, and caps nesting at depth 3.

const MAX_DEPTH = 3

type Props = {
  group: GroupNode
  catalog: CustomFieldCatalog
  depth: number
  onChange: (next: GroupNode) => void
  onRemove?: () => void
  disabled?: boolean
}

export function PredicateGroup({ group, catalog, depth, onChange, onRemove, disabled }: Props) {
  const isRoot = depth === 0

  const updateChild = (idx: number, next: PredicateNode) => {
    const children = group.children.map((c, i) => (i === idx ? next : c))
    onChange({ ...group, children })
  }

  const removeChild = (idx: number) => {
    const children = group.children.filter((_, i) => i !== idx)
    onChange({ ...group, children })
  }

  const addCondition = () => {
    // Default scope = appuser, default field = first available live custom
    // field, falling back to a placeholder leaf the user fills in.
    const firstAppuser = catalog.appuser.find((d) => d.status === "live")
    const fresh = firstAppuser
      ? defaultLeaf("appuser", {
          key: firstAppuser.key,
          fieldType: firstAppuser.fieldType,
          options: firstAppuser.options,
        })
      : placeholderLeaf("appuser")
    onChange({ ...group, children: [...group.children, fresh] })
  }

  const addGroup = () => {
    if (depth + 1 >= MAX_DEPTH) return
    // Seed the sub-group with one default condition. An empty group
    // serializes to `{}` and gets stripped on the JSON round-trip in
    // PredicateBuilder, which would silently undo this click.
    const firstAppuser = catalog.appuser.find((d) => d.status === "live")
    const seed = firstAppuser
      ? defaultLeaf("appuser", {
          key: firstAppuser.key,
          fieldType: firstAppuser.fieldType,
          options: firstAppuser.options,
        })
      : placeholderLeaf("appuser")
    const sub: GroupNode = {
      ...defaultGroup(group.op === "all" ? "any" : "all"),
      children: [seed],
    }
    onChange({
      ...group,
      children: [...group.children, sub],
    })
  }

  const setOp = (next: CompositeOp) => {
    onChange({ ...group, op: next })
  }

  const canNest = depth + 1 < MAX_DEPTH

  return (
    <div
      className={cn(
        "space-y-2 rounded-card border border-border p-3",
        isRoot ? "bg-app-panel/40" : "bg-white/[0.02]",
      )}
    >
      {/* Header — ALL/ANY toggle + remove (only on nested groups) */}
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1 rounded-control border border-border bg-white/5 p-0.5 text-[11px] font-medium">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOp("all")}
            className={cn(
              "rounded-control px-2 py-1 transition",
              group.op === "all" ? "bg-brand-red text-white" : "text-app-muted hover:text-app-text",
            )}
          >
            ALL (AND)
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOp("any")}
            className={cn(
              "rounded-control px-2 py-1 transition",
              group.op === "any" ? "bg-brand-red text-white" : "text-app-muted hover:text-app-text",
            )}
          >
            ANY (OR)
          </button>
        </div>
        {!isRoot && onRemove && (
          <button
            type="button"
            disabled={disabled}
            onClick={onRemove}
            className="text-[11px] text-status-error hover:underline disabled:opacity-40 disabled:hover:no-underline"
          >
            Remove group
          </button>
        )}
      </div>

      {/* Children */}
      {group.children.length === 0 ? (
        <div className="rounded-control border border-dashed border-border px-3 py-4 text-center text-[11px] text-app-muted">
          No conditions yet. Use {`+ Add condition`} below.
        </div>
      ) : (
        <div className="space-y-2">
          {group.children.map((child, i) => (
            <ChildRow
              key={child.id}
              child={child}
              catalog={catalog}
              depth={depth}
              onChange={(next) => updateChild(i, next)}
              onRemove={() => removeChild(i)}
              disabled={disabled}
              joinerLabel={i === 0 ? null : group.op === "all" ? "AND" : "OR"}
            />
          ))}
        </div>
      )}

      {/* Footer — add buttons */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          disabled={disabled}
          onClick={addCondition}
          className="inline-flex items-center gap-1 rounded-control border border-border bg-white/5 px-2.5 py-1 text-[11px] text-app-text hover:bg-white/10 disabled:opacity-40"
        >
          <Plus size={12} />
          Add condition
        </button>
        {canNest && (
          <button
            type="button"
            disabled={disabled}
            onClick={addGroup}
            className="inline-flex items-center gap-1 rounded-control border border-border bg-white/5 px-2.5 py-1 text-[11px] text-app-text hover:bg-white/10 disabled:opacity-40"
          >
            <Plus size={12} />
            Add group
          </button>
        )}
      </div>
    </div>
  )
}

function ChildRow({
  child, catalog, depth, onChange, onRemove, disabled, joinerLabel,
}: {
  child: PredicateNode
  catalog: CustomFieldCatalog
  depth: number
  onChange: (next: PredicateNode) => void
  onRemove: () => void
  disabled?: boolean
  joinerLabel: string | null
}) {
  return (
    <div className="space-y-1.5">
      {joinerLabel && (
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-app-subtle">
          <span className="rounded-chip border border-border bg-app-panel2 px-1.5 py-0.5">{joinerLabel}</span>
          <div className="h-px flex-1 bg-border/40" />
        </div>
      )}
      {child.kind === "leaf" ? (
        <PredicateRow
          leaf={child as LeafNode}
          catalog={catalog}
          onChange={onChange}
          onRemove={onRemove}
          disabled={disabled}
        />
      ) : (
        <PredicateGroup
          group={child as GroupNode}
          catalog={catalog}
          depth={depth + 1}
          onChange={onChange}
          onRemove={onRemove}
          disabled={disabled}
        />
      )}
    </div>
  )
}

function placeholderLeaf(scope: Scope): LeafNode {
  return {
    kind: "leaf",
    id: `l_${Math.random().toString(36).slice(2, 9)}`,
    scope,
    fieldKey: "",
    fieldType: "text",
    op: "eq",
    value: "",
  }
}
